import {getAllPossibleTypenamesForPrefix} from 'graphql-client/APIIDPrefixToTypename';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {getAPIIDPrefix} from 'util/APIUtil';

/**
 * Updates the cached `name` field for the item with the given API `id`, if it's present in the
 * Apollo cache.
 *
 * This keeps GraphQL-backed views (e.g. search listings) in sync after a rename, without needing
 * to enumerate every renameable GraphQL type up front or know which query/fragment originally
 * wrote the cache entry. Any type reachable from `id`'s prefix via APIIDPrefixToTypename.json5
 * (including known ambiguous-prefix candidates, e.g. the 'bfi' prefix shared by
 * BasicFolderItemNode/EquipmentNode/etc.) is covered automatically; ids with a completely unmapped
 * prefix are silently skipped, since we can't determine which cache entry to update without more
 * context.
 *
 * `cache.modify` is a no-op when the id has no matching cache entry, or when that entry doesn't
 * already have a `name` field cached, so it's safe to call this for every candidate typename and
 * every rename, regardless of whether (or how) the item is currently present in the GraphQL cache.
 */
export default function syncItemNameToApolloCache(id: string, name: string): void {
  const prefix = getAPIIDPrefix(id);
  if (prefix == null) {
    return;
  }

  const {cache} = getApolloClient();
  for (const __typename of getAllPossibleTypenamesForPrefix(prefix)) {
    const dataId = cache.identify({__typename, id});
    // cache.identify can return undefined (e.g. for a type normalized with non-default keyFields).
    // cache.modify defaults a missing/undefined id to ROOT_QUERY, so skip explicitly rather than
    // risk overwriting an unrelated top-level `name` field.
    if (dataId == null) {
      continue;
    }
    cache.modify({
      id: dataId,
      fields: {name: () => name},
    });
  }
}
